import React, { useState } from 'react';
import { X, Upload, FileText, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import axios from 'axios';
import { uploadsApi } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClarificationForm = ({ clarification, registration, onClose, onSubmitted }) => {
  const [fieldResponses, setFieldResponses] = useState({});
  const [userDescription, setUserDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({}); // { label: { file_url, original_filename } }
  const [uploading, setUploading] = useState({});

  // Initialize field responses with current values
  React.useEffect(() => {
    const initialResponses = {};
    const invalidFields = clarification.invalid_fields || [];
    
    invalidFields.forEach(({ field }) => {
      // Get current value from registration
      let currentValue = '';
      
      if (registration.registration_type === 'company') {
        const companyInfo = registration.company_info || {};
        const financialInfo = registration.financial_info || {};
        const complianceInfo = registration.compliance_info || {};
        const branches = registration.branches || [];
        const tradeReferences = registration.trade_references || [];
        
        // Handle trade reference fields (trade_ref_0_company_name)
        if (field.startsWith('trade_ref_')) {
          const parts = field.split('_');
          if (parts.length >= 4 && parts[0] === 'trade' && parts[1] === 'ref') {
            try {
              const refIndex = parseInt(parts[2]);
              const fieldSuffix = parts.slice(3).join('_');
              
              // Skip computed fields
              if (fieldSuffix === 'mobile') {
                // mobile is computed from country_code + mobile, skip it
                currentValue = '';
              } else {
                const refField = fieldSuffix; // Most fields match directly
                if (tradeReferences[refIndex] && tradeReferences[refIndex][refField] !== undefined) {
                  currentValue = tradeReferences[refIndex][refField];
                }
              }
            } catch (e) {
              // Ignore parsing errors
            }
          }
        }
        // Handle branch fields (branch_0_name)
        else if (field.startsWith('branch_')) {
          const parts = field.split('_');
          if (parts.length >= 3 && parts[0] === 'branch') {
            try {
              const branchIndex = parseInt(parts[1]);
              const fieldSuffix = parts.slice(2).join('_');
              
              // Map simplified field names to actual database field names
              // Admin sends: branch_0_name, branch_0_address, etc.
              // Database has: branch_name, street_address, etc.
              const fieldMapping = {
                'name': 'branch_name',
                'address': 'street_address',
                'contact_phone': null, // Computed field, skip
              };
              
              const branchField = fieldMapping[fieldSuffix] || fieldSuffix;
              
              if (branchField && branches[branchIndex] && branches[branchIndex][branchField] !== undefined) {
                currentValue = branches[branchIndex][branchField];
              }
            } catch (e) {
              // Ignore parsing errors
            }
          }
        }
        // Skip computed fields that can't be updated directly
        else if (['primary_contact_mobile', 'secondary_contact_mobile'].includes(field)) {
          // These are computed display fields (country_code + mobile), skip them
          currentValue = '';
        }
        // Check nested paths
        else if (field.startsWith('company_info.')) {
          const nestedKey = field.replace('company_info.', '');
          currentValue = companyInfo[nestedKey] || '';
        }
        else if (field.startsWith('financial_info.')) {
          const nestedKey = field.replace('financial_info.', '');
          currentValue = financialInfo[nestedKey] || '';
        }
        else if (field.startsWith('compliance_info.')) {
          const nestedKey = field.replace('compliance_info.', '');
          currentValue = complianceInfo[nestedKey] || '';
        }
        // Check in company_info
        else if (companyInfo[field] !== undefined) {
          currentValue = companyInfo[field];
        }
        // Check in financial_info
        else if (financialInfo[field] !== undefined) {
          currentValue = financialInfo[field];
        }
        // Check in compliance_info
        else if (complianceInfo[field] !== undefined) {
          currentValue = complianceInfo[field];
        }
      } else {
        // Simple registration
        currentValue = registration[field] || '';
      }
      
      // Convert boolean/numbers to string for display
      if (currentValue === true) currentValue = 'Yes';
      if (currentValue === false) currentValue = 'No';
      if (typeof currentValue === 'number') currentValue = String(currentValue);
      
      initialResponses[field] = currentValue || '';
    });
    
    setFieldResponses(initialResponses);
  }, [clarification, registration]);

  const handleFieldChange = (field, value) => {
    setFieldResponses({
      ...fieldResponses,
      [field]: value
    });
  };

  const handleFileUpload = async (label, file) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [label]: true }));
    try {
      const res = await uploadsApi.uploadFile(file, 'clarifications', clarification.clarification_id);
      const data = res.data;
      const fileUrl = data.file_url || data.file?.url || data.url;
      setUploadedFiles((prev) => ({
        ...prev,
        [label]: { file_url: fileUrl, original_filename: file.name }
      }));
    } catch (err) {
      toast.error(`Failed to upload ${file.name}`);
    } finally {
      setUploading((prev) => ({ ...prev, [label]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that all invalid fields have responses
    const invalidFields = clarification.invalid_fields || [];
    const missingFields = invalidFields.filter(({ field }) => {
      if (isDocumentField(field)) {
        // Document fields require a file upload
        return !uploadedFiles[field]?.file_url;
      }
      const response = fieldResponses[field];
      return !response || response.trim() === '';
    });

    if (missingFields.length > 0) {
      const labels = missingFields.map(f => getFieldLabel(f.field));
      toast.error(`Please complete all required fields: ${labels.join(', ')}`);
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Build uploaded_files array from all uploads (both document re-uploads and requested files)
      const filesArray = Object.entries(uploadedFiles)
        .filter(([, f]) => f?.file_url)
        .map(([key, f]) => ({
          label: isDocumentField(key) ? getFieldLabel(key) : key,
          field: key,
          file_url: f.file_url,
          original_filename: f.original_filename
        }));

      await axios.post(
        `${API_URL}/api/clarifications/${clarification.clarification_id}/respond`,
        {
          field_responses: fieldResponses,
          user_description: userDescription,
          uploaded_files: filesArray.length > 0 ? filesArray : undefined
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success('Clarification submitted successfully');
      onSubmitted();
    } catch (error) {
      console.error('Failed to submit clarification:', error);
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to submit clarification'));
    } finally {
      setSubmitting(false);
    }
  };

  const isDocumentField = (field) => field.startsWith('doc_');

  const getFieldLabel = (field) => {
    // For document fields, strip the doc_ prefix and format nicely
    const raw = field.startsWith('doc_') ? field.replace(/^doc_/, '') : field;
    return raw
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace(/company info\./gi, '')
      .replace(/financial info\./gi, '')
      .replace(/compliance info\./gi, '');
  };

  const invalidFields = clarification.invalid_fields || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-xl font-semibold">Clarification Request</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X size={20} />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Admin Notes:</strong> {clarification.validation_notes || 'Please review and correct the following fields.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Invalid Fields */}
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Fields Requiring Clarification</h3>
                <div className="space-y-4">
                  {invalidFields.map(({ field, reason }, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="mb-2">
                        <Label className="font-medium text-gray-900">
                          {getFieldLabel(field)} *
                        </Label>
                        <p className="text-xs text-red-600 mt-1">
                          <strong>Reason:</strong> {reason}
                        </p>
                      </div>
                      {isDocumentField(field) ? (
                        /* File upload + text input for document fields */
                        <div className="mt-2 space-y-3">
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block">Upload Updated File</Label>
                            {uploadedFiles[field] ? (
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="flex items-center gap-1">
                                  <FileText size={12} />
                                  {uploadedFiles[field].original_filename}
                                </Badge>
                                <Button type="button" size="sm" variant="ghost" className="text-red-500 px-2"
                                  onClick={() => setUploadedFiles((prev) => { const next = { ...prev }; delete next[field]; return next; })}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 p-3 border-2 border-dashed rounded-lg hover:border-blue-300 transition-colors">
                                <Upload size={18} className="text-gray-400" />
                                <label className="cursor-pointer text-sm text-blue-600 hover:text-blue-700">
                                  Choose file to upload
                                  <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                                    onChange={(e) => handleFileUpload(field, e.target.files?.[0])}
                                    disabled={uploading[field]}
                                    className="hidden"
                                  />
                                </label>
                                {uploading[field] && <span className="text-xs text-gray-500">Uploading...</span>}
                              </div>
                            )}
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500 mb-1 block">Additional Comments (Optional)</Label>
                            <Input
                              value={fieldResponses[field] || ''}
                              onChange={(e) => handleFieldChange(field, e.target.value)}
                              placeholder={`Add a note about ${getFieldLabel(field)}...`}
                            />
                          </div>
                        </div>
                      ) : (
                        /* Text input for regular fields */
                        <Input
                          value={fieldResponses[field] || ''}
                          onChange={(e) => handleFieldChange(field, e.target.value)}
                          placeholder={`Enter ${getFieldLabel(field)}`}
                          className="mt-2"
                          required
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Requested File Uploads */}
              {clarification.requested_files && clarification.requested_files.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-4">
                    <Upload size={18} className="inline mr-2" />
                    Requested File Uploads
                  </h3>
                  <div className="space-y-4">
                    {clarification.requested_files.map((label, idx) => (
                      <div key={idx} className="p-4 border rounded-lg">
                        <Label className="font-medium text-gray-900 mb-2 block">{label} *</Label>
                        {uploadedFiles[label] ? (
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <FileText size={12} />
                              {uploadedFiles[label].original_filename}
                            </Badge>
                            <Button size="sm" variant="ghost" className="text-red-500 px-2"
                              onClick={() => setUploadedFiles((prev) => { const next = { ...prev }; delete next[label]; return next; })}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                              onChange={(e) => handleFileUpload(label, e.target.files?.[0])}
                              disabled={uploading[label]}
                              className="text-sm"
                            />
                            {uploading[label] && <p className="text-xs text-gray-500 mt-1">Uploading...</p>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* User Description */}
              <div>
                <Label className="font-medium text-gray-900 mb-2 block">
                  Additional Description (Optional)
                </Label>
                <Textarea
                  value={userDescription}
                  onChange={(e) => setUserDescription(e.target.value)}
                  placeholder="If you have any additional information or comments, please provide them here..."
                  rows={4}
                  className="w-full"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Clarification'}
                </Button>
              </div>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClarificationForm;
